import { formatPercent } from '@/ui/formatters/formatPercent';
import { formatStatusLabel, resolveStatusChipTone } from '@/ui/primitives';
import type { StatusChipStatus } from '@/ui/primitives';
import type { StatusTone } from '@/ui/primitives/Chip';
import type { ReactNode } from 'react';

type MetricCardTone = 'success' | 'warning' | 'error' | 'info' | 'brown' | 'primary' | 'default';
type MetricCardDeltaTone = MetricCardItem['deltaTone'];

export type MetricCardItem = {
  id: string;
  status: ReactNode;
  statusTone: MetricCardTone;
  count: ReactNode;
  secondaryValue?: ReactNode;
  percentage: ReactNode;
  delta?: ReactNode;
  deltaTone?: 'positive' | 'negative' | 'neutral';
  countDelta?: ReactNode;
  countDeltaTone?: 'positive' | 'negative' | 'neutral';
  percentageDelta?: ReactNode;
  percentageDeltaTone?: 'positive' | 'negative' | 'neutral';
};

export type MetricCardOption<TStatus extends string> = {
  value: TStatus;
  label?: string;
  tone?: MetricCardTone;
};

export type BuildMetricCardsArgs<TStatus extends string> = {
  options: Array<MetricCardOption<TStatus>>;
  counts: Partial<Record<TStatus, number>>;
  deltas?: Partial<Record<TStatus, number>>;
  total: number | null | undefined;
  previousCounts?: Partial<Record<TStatus, number>>;
  previousTotal?: number | null | undefined;
  loading: boolean;
  resolveTone?: (status: TStatus) => MetricCardTone;
  showDelta?: boolean;
  resolveDeltaTone?: (delta: number) => MetricCardItem['deltaTone'];
  showTotalCard?: boolean;
  totalLabel?: string;
  totalSecondaryValue?: ReactNode;
  hideZeroCountStatuses?: TStatus[];
};

const normalizeMetricTone = (tone: StatusTone): MetricCardTone => {
  switch (tone) {
    case 'neutral':
    case 'default':
    case 'grey':
      return 'default';
    case 'green':
    case 'success':
      return 'success';
    case 'red':
    case 'error':
      return 'error';
    case 'orange':
    case 'yellow':
    case 'warning':
      return 'warning';
    case 'blue':
    case 'info':
      return 'info';
    case 'teal':
      return 'primary';
    case 'transparent':
      return 'default';
    case 'brown':
      return 'brown';
    case 'primary':
    case 'purple':
    case 'pink':
      return 'primary';
  }
};

export const resolveMetricTone = (status: StatusChipStatus | 'none'): MetricCardTone => {
  if (status === 'none') return 'default';
  return normalizeMetricTone(resolveStatusChipTone(status));
};

export const mapStatusOptionsToMetricOptions = <TStatus extends string>(
  options: Array<{ value: TStatus; label: string }>,
  toneResolver: (status: TStatus) => MetricCardTone,
): Array<MetricCardOption<TStatus>> =>
  options.map((option) => ({
    value: option.value,
    label: option.label,
    tone: toneResolver(option.value),
  }));

const formatCount = (value: number, showPlaceholder: boolean): ReactNode => {
  if (showPlaceholder) return '-';
  return value.toLocaleString();
};

const formatPercentage = (count: number, total: number, showPlaceholder: boolean): ReactNode => {
  if (showPlaceholder) return '-';
  if (total <= 0) return '0%';
  return `${formatPercent((count / total) * 100)}%`;
};

export const formatRelatedEntityCountLabel = (
  value: number | null | undefined,
  singularLabel: string,
  pluralLabel?: string,
): string => {
  const resolvedValue = value ?? 0;
  const resolvedSingularLabel = singularLabel.toLowerCase();
  const resolvedPluralLabel = (pluralLabel ?? `${singularLabel}s`).toLowerCase();
  return `${resolvedValue.toLocaleString()} ${resolvedValue === 1 ? resolvedSingularLabel : resolvedPluralLabel}`;
};

const sumMetricValues = (values: Array<number | undefined>): number =>
  values.reduce<number>((sum, value) => sum + (value ?? 0), 0);

const isZeroLikeDelta = (value: number | undefined): boolean =>
  value === undefined || !Number.isFinite(value) || Object.is(value, 0) || Object.is(value, -0);

const invertDeltaTone = (tone: MetricCardDeltaTone): MetricCardDeltaTone => {
  if (tone === 'neutral') return tone;
  return tone === 'positive' ? 'negative' : 'positive';
};

const toRoundedPercentDelta = (
  current: number,
  previous: number | undefined,
): number | undefined => {
  if (previous === undefined || previous <= 0) return undefined;
  return Math.round(((current - previous) / previous) * 100 * 100) / 100;
};

export const buildStatusMetricCards = <TStatus extends string>({
  options,
  counts,
  deltas,
  total,
  previousCounts,
  previousTotal,
  loading,
  resolveTone,
  showDelta = false,
  resolveDeltaTone,
  showTotalCard = true,
  totalLabel = 'TOTAL',
  totalSecondaryValue,
  hideZeroCountStatuses,
}: BuildMetricCardsArgs<TStatus>): MetricCardItem[] => {
  const showPlaceholder = loading && (total === null || total === undefined);
  const totalCount = total ?? sumMetricValues(Object.values(counts) as Array<number | undefined>);
  const hiddenStatuses = new Set(hideZeroCountStatuses ?? []);
  const cards = options.map((option) => {
    const countValue = counts[option.value] ?? 0;
    const label = option.label ?? formatStatusLabel(option.value as StatusChipStatus);
    const tone = option.tone ?? (resolveTone ? resolveTone(option.value) : 'default');
    const rawDelta = deltas?.[option.value];
    const shouldShowDelta = showDelta;
    const resolvedDelta =
      rawDelta === undefined || rawDelta === 0
        ? shouldShowDelta
          ? '-'
          : undefined
        : rawDelta > 0
          ? `+${rawDelta}`
          : rawDelta;
    const baseDeltaTone =
      resolvedDelta === undefined || resolvedDelta === '-'
        ? 'neutral'
        : resolveDeltaTone
          ? resolveDeltaTone(rawDelta ?? 0)
          : rawDelta && rawDelta > 0
            ? 'positive'
            : 'negative';
    const shouldInvertDeltaTone = option.value === 'failed' || option.value === 'errored';
    const deltaTone =
      shouldInvertDeltaTone && baseDeltaTone !== 'neutral'
        ? baseDeltaTone === 'positive'
          ? 'negative'
          : 'positive'
        : baseDeltaTone;
    const previousCountValue = previousCounts?.[option.value] ?? 0;
    const currentPercentage = totalCount > 0 ? (countValue / totalCount) * 100 : 0;
    const previousPercentage =
      previousTotal && previousTotal > 0 ? (previousCountValue / previousTotal) * 100 : 0;
    const rawPercentageDelta =
      previousCounts && previousTotal !== null && previousTotal !== undefined
        ? currentPercentage - previousPercentage
        : undefined;
    const roundedPercentageDelta =
      rawPercentageDelta === undefined ? undefined : Math.round(rawPercentageDelta * 100) / 100;
    const relativeCountPercentageDelta =
      totalCount === countValue ? toRoundedPercentDelta(countValue, previousCountValue) : undefined;
    const resolvedPercentageDeltaValue = !showDelta
      ? undefined
      : isZeroLikeDelta(roundedPercentageDelta)
        ? relativeCountPercentageDelta
        : roundedPercentageDelta;
    const resolvedPercentageDelta = (() => {
      const percentageDeltaValue = resolvedPercentageDeltaValue;
      if (percentageDeltaValue === undefined) return undefined;
      return percentageDeltaValue > 0 ? `+${percentageDeltaValue}%` : `${percentageDeltaValue}%`;
    })();
    const basePercentageDeltaTone: MetricCardDeltaTone =
      resolvedPercentageDelta === undefined || resolvedPercentageDelta === '-'
        ? 'neutral'
        : (resolvedPercentageDeltaValue ?? 0) > 0
          ? 'positive'
          : 'negative';
    const percentageDeltaTone: MetricCardDeltaTone =
      shouldInvertDeltaTone && basePercentageDeltaTone !== 'neutral'
        ? invertDeltaTone(basePercentageDeltaTone)
        : basePercentageDeltaTone;
    return {
      id: option.value,
      status: label,
      statusTone: tone,
      count: formatCount(countValue, showPlaceholder),
      percentage: formatPercentage(countValue, totalCount, showPlaceholder),
      delta: resolvedDelta,
      deltaTone,
      countDelta: resolvedDelta,
      countDeltaTone: deltaTone,
      percentageDelta: resolvedPercentageDelta,
      percentageDeltaTone: percentageDeltaTone,
    };
  });
  const visibleCards = cards.filter((card) => {
    if (!hiddenStatuses.has(card.id as TStatus)) {
      return true;
    }
    return (counts[card.id as TStatus] ?? 0) > 0;
  });
  if (!showTotalCard) {
    return visibleCards;
  }
  const totalDelta =
    showDelta && deltas
      ? sumMetricValues(Object.values(deltas) as Array<number | undefined>)
      : undefined;
  const hasNoTotalData = totalCount === 0 && (previousTotal ?? 0) === 0;
  const totalPercentageDelta =
    showDelta &&
    previousTotal !== null &&
    previousTotal !== undefined &&
    previousTotal > 0 &&
    totalCount > 0
      ? Math.round(((totalCount - previousTotal) / previousTotal) * 100 * 100) / 100
      : undefined;
  let totalDeltaResolved: string | number | undefined;
  if (hasNoTotalData) {
    totalDeltaResolved = undefined;
  } else if (totalDelta === undefined || totalDelta === 0) {
    totalDeltaResolved = showDelta ? '-' : undefined;
  } else {
    totalDeltaResolved = totalDelta > 0 ? `+${totalDelta}` : totalDelta;
  }
  const totalDeltaTone =
    totalDeltaResolved === undefined || totalDeltaResolved === '-'
      ? 'neutral'
      : (totalDelta ?? 0) > 0
        ? 'positive'
        : 'negative';
  const totalPercentageDeltaResolved = (() => {
    if (isZeroLikeDelta(totalPercentageDelta)) return undefined;
    const percentageDeltaValue = totalPercentageDelta;
    if (percentageDeltaValue === undefined) return undefined;
    return percentageDeltaValue > 0 ? `+${percentageDeltaValue}%` : `${percentageDeltaValue}%`;
  })();
  const totalPercentageDeltaTone: MetricCardDeltaTone =
    totalPercentageDeltaResolved === undefined || totalPercentageDeltaResolved === '-'
      ? 'neutral'
      : (totalPercentageDelta ?? 0) > 0
        ? 'positive'
        : 'negative';
  return [
    {
      id: 'total',
      status: totalLabel,
      statusTone: 'primary',
      count: formatCount(totalCount, showPlaceholder),
      secondaryValue: totalSecondaryValue,
      percentage: undefined,
      delta: totalDeltaResolved,
      deltaTone: totalDeltaTone,
      countDelta: totalDeltaResolved,
      countDeltaTone: totalDeltaTone,
      percentageDelta: totalPercentageDeltaResolved,
      percentageDeltaTone: totalPercentageDeltaTone,
    },
    ...visibleCards,
  ];
};

export const buildStatusDeltaMap = <TStatus extends string>(
  current: Partial<Record<TStatus, number>>,
  previous: Partial<Record<TStatus, number>>,
): Partial<Record<TStatus, number>> => {
  const deltas: Partial<Record<TStatus, number>> = {};
  const keys = new Set<string>([
    ...(Object.keys(current) as TStatus[]),
    ...(Object.keys(previous) as TStatus[]),
  ]);
  keys.forEach((key) => {
    const typedKey = key as TStatus;
    const currentValue = current[typedKey] ?? 0;
    const previousValue = previous[typedKey] ?? 0;
    deltas[typedKey] = currentValue - previousValue;
  });
  return deltas;
};
